"use client";

import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@cartridge/controller-ui";
import {
  useAccount,
  useConnect,
  useDisconnect,
  useNetwork,
  useSwitchChain,
} from "@starknet-start/react";
import { useState, useEffect, useRef, useMemo } from "react";
import { constants, num, shortString } from "starknet";
import { Chain } from "@starknet-start/chains";
import { HeadlessLogin } from "components/HeadlessLogin";
import {
  ConnectOptions,
  controllerConnector,
  presets,
} from "./providers/StarknetProvider";

type HeadlessModalState = "closed" | "open" | "hidden";

const Header = () => {
  const { connect, connectors } = useConnect();
  const { disconnect } = useDisconnect();
  const { chain, chains } = useNetwork();
  const { address, status } = useAccount();
  const [connectChainOpen, setConnectChainOpen] = useState(false);
  const [networkOpen, setNetworkOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [headlessState, setHeadlessState] =
    useState<HeadlessModalState>("closed");
  const [isControllerReady, setIsControllerReady] = useState(false);
  const [overridePolicies, setOverridePolicies] = useState(false);
  const [presetValue, setPresetValue] = useState<string>("none");

  useEffect(() => {
    setConnectChainOpen(false);
    setNetworkOpen(false);
    setProfileOpen(false);
  }, [address]);

  useEffect(() => {
    setOverridePolicies(
      window.localStorage.getItem(ConnectOptions.OverridePolicies) === "true",
    );
    setPresetValue(
      window.localStorage.getItem(ConnectOptions.Preset) || "none",
    );
  }, []);
  const { switchChain } = useSwitchChain({
    params: {
      chainId: constants.StarknetChainId.SN_SEPOLIA,
    },
  });
  const networkRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const controllerWallet = useMemo(
    () => connectors.find((connector) => connector.name === "Controller"),
    [connectors],
  );

  useEffect(() => {
    const checkReady = () => {
      try {
        setIsControllerReady(controllerConnector.isReady());
      } catch (e) {
        console.error("Error checking controller readiness:", e);
      }
    };

    checkReady();

    const interval = setInterval(checkReady, 1000);

    return () => clearInterval(interval);
  }, []);

  const sessionWallet = useMemo(
    () =>
      connectors.find((connector) => connector.name === "Controller Session"),
    [connectors],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        networkRef.current &&
        !networkRef.current.contains(event.target as Node)
      ) {
        setNetworkOpen(false);
      }
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <div className="w-full p-0 flex flex-wrap items-center gap-2">
        <div className="flex-1" />

        {status === "connected" && (
          <>
            <div className="relative" ref={networkRef}>
              <Button
                onClick={() => {
                  setNetworkOpen(!networkOpen);
                  setProfileOpen(false);
                }}
                className="flex items-center gap-2 min-w-[120px]"
              >
                {chain.network}
                <span
                  className={`transition-transform duration-200 ${
                    networkOpen ? "rotate-180" : ""
                  }`}
                >
                  ▼
                </span>
              </Button>
              {networkOpen && (
                <div className="absolute right-0 top-full mt-1 bg-background shadow-lg rounded-md min-w-[160px] py-1 z-10 border border-gray-600">
                  {chains.map((c: Chain) => (
                    <button
                      key={c.id}
                      className="block w-full px-4 py-2 text-left hover:bg-gray-600 transition-colors border-b border-gray-600 last:border-0"
                      onClick={() => {
                        switchChain({ chainId: num.toHex(c.id) });
                        setNetworkOpen(false);
                      }}
                    >
                      {c.network}
                    </button>
                  ))}
                  <button
                    className="block w-full px-4 py-2 text-left hover:bg-gray-600 transition-colors border-b border-gray-600 last:border-0"
                    onClick={() => {
                      switchChain({
                        chainId: shortString.encodeShortString("UNSUPPORTED"),
                      });
                      setNetworkOpen(false);
                    }}
                  >
                    unsupported
                  </button>
                </div>
              )}
            </div>
          </>
        )}
        {address ? (
          <div className="relative" ref={profileRef}>
            <Button
              onClick={() => {
                setProfileOpen(!profileOpen);
                setNetworkOpen(false);
              }}
              className="flex items-center gap-2 min-w-[120px]"
            >
              <strong className="truncate max-w-[120px]">{address}</strong>
              <span
                className={`transition-transform duration-200 ${
                  profileOpen ? "rotate-180" : ""
                }`}
              >
                ▼
              </span>
            </Button>
            {profileOpen && (
              <div className="absolute right-0 top-full mt-1 bg-background shadow-lg rounded-md min-w-[160px] py-1 z-10 border border-gray-600">
                <button
                  className="block w-full px-4 py-2 text-left hover:bg-gray-600 transition-colors border-b border-gray-600"
                  onClick={() => controllerConnector.controller.openProfile()}
                >
                  Profile
                </button>
                <button
                  className="block w-full px-4 py-2 text-left hover:bg-gray-600 transition-colors border-b border-gray-600"
                  onClick={() => controllerConnector.controller.openSettings()}
                >
                  Settings
                </button>
                <button
                  className="block w-full px-4 py-2 text-left hover:bg-gray-600 transition-colors"
                  onClick={() => disconnect()}
                >
                  Disconnect
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => {
                controllerConnector.controller.open({
                  redirectUrl: window.location.href,
                });
              }}
              disabled={!isControllerReady}
            >
              Standalone
            </Button>
            <Button
              onClick={() => setHeadlessState("open")}
              disabled={!isControllerReady}
            >
              Headless
            </Button>
            <Button
              onClick={() => {
                if (controllerWallet) connect({ connector: controllerWallet });
              }}
              disabled={!isControllerReady || !controllerWallet}
            >
              {isControllerReady ? "Connect" : "Waiting for keychain..."}
            </Button>
            <Button
              onClick={() => {
                controllerConnector.connect({
                  signupOptions: ["phantom-evm"],
                });
              }}
              disabled={!isControllerReady}
              className="bg-[#AB9FF2] hover:bg-[#9B8FE2] text-white"
            >
              Phantom
            </Button>
            {sessionWallet && (
              <Button
                onClick={() => connect({ connector: sessionWallet })}
                disabled={!isControllerReady}
              >
                Register Session
              </Button>
            )}
          </div>
        )}

        {headlessState !== "closed" && (
          <div
            className={
              headlessState === "open"
                ? "fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
                : "hidden"
            }
            onClick={() => setHeadlessState("closed")}
          >
            <div
              className="relative w-full max-w-xl"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setHeadlessState("closed")}
                className="absolute -top-3 -right-3 rounded-full bg-white px-3 py-1 text-sm font-medium text-gray-700 shadow hover:bg-gray-100"
              >
                Close
              </button>
              <HeadlessLogin
                onStart={() => setHeadlessState("hidden")}
                onDone={() => setHeadlessState("closed")}
                onError={() => setHeadlessState("open")}
              />
            </div>
          </div>
        )}
      </div>

      {!address && (
        <div className="w-full p-0 flex flex-wrap items-center gap-2">
          <div className="flex-grow" />
          <h1>Connect options:</h1>

          {!address && (
            <>
              <div className="relative" ref={networkRef}>
                <Button
                  disabled={!isControllerReady}
                  variant="secondary"
                  onClick={() => {
                    setConnectChainOpen(!connectChainOpen);
                  }}
                >
                  {shortString.decodeShortString(num.toHex(chain.id))}
                  <span
                    className={`transition-transform duration-200 ${
                      connectChainOpen ? "rotate-180" : ""
                    }`}
                  >
                    ▼
                  </span>
                </Button>
                {connectChainOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-background shadow-lg rounded-md min-w-[160px] py-1 z-10 border border-gray-600">
                    {chains.map((c: Chain) => (
                      <button
                        key={c.id}
                        className="block w-full px-4 py-2 text-left hover:bg-gray-600 transition-colors border-b border-gray-600 last:border-0"
                        onClick={() => {
                          window.localStorage.setItem(
                            ConnectOptions.DefaultChainId,
                            num.toHex(c.id),
                          );
                          window.location.reload();
                        }}
                      >
                        {shortString.decodeShortString(num.toHex(c.id))}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          <Button
            disabled={!isControllerReady}
            variant="secondary"
            onClick={() => {
              if (
                window.localStorage.getItem(ConnectOptions.OverridePolicies) ===
                "true"
              ) {
                window.localStorage.removeItem(ConnectOptions.OverridePolicies);
              } else {
                window.localStorage.setItem(
                  ConnectOptions.OverridePolicies,
                  "true",
                );
              }
              window.location.reload();
            }}
          >
            {overridePolicies ? "Override Policies" : "Preset Policies"}
          </Button>
          <div className="w-[150px]">
            <Select
              value={presetValue}
              onValueChange={(v: string) => {
                if (v === "none") {
                  window.localStorage.removeItem(ConnectOptions.Preset);
                } else {
                  window.localStorage.setItem(ConnectOptions.Preset, v);
                }
                window.location.reload();
              }}
              disabled={!isControllerReady}
            >
              <SelectTrigger>
                <SelectValue placeholder="Preset:" />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(presets).map((preset) => (
                  <SelectItem key={preset} value={preset}>
                    {preset === "none" ? "no preset" : preset}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
